import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { socialLinks } from '../data/contact';
import styles from '../styles/Contact.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          message: formState.message,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSubmitted(true);
      setFormState({ name: '', email: '', message: '' });
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section className={styles.contact} id="contact">
      <div className={styles.contact__container}>
        <motion.div 
          className={styles.contact__wrapper}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Left Column: Info */}
          <div className={styles.contact__info}>
            <motion.h2 className={styles.contact__heading} variants={itemVariants}>
              Get in Touch
            </motion.h2>
            
            <motion.p className={styles.contact__subtitle} variants={itemVariants}>
              Have a project in mind or just want to chat? I'm always open to discussing new opportunities and ideas.
            </motion.p>
            
            <motion.div className={styles.contact__socials} variants={itemVariants}>
              {socialLinks.map((link) => (
                <a 
                  key={link.id}
                  href={link.url}
                  target={link.url.startsWith('mailto') ? undefined : "_blank"}
                  rel={link.url.startsWith('mailto') ? undefined : "noopener noreferrer"}
                  className={styles.contact__social_link} 
                  aria-label={link.label}
                >
                  <link.icon />
                </a>
              ))}
            </motion.div>
          </div>

          {/* Right Column: Form */}
          <div className={styles.contact__form_container}>
             <motion.form
               ref={formRef}
               className={styles.contact__form}
               onSubmit={handleSubmit}
               initial={{ opacity: 0, x: 20 }}
               whileInView={{ opacity: 1, x: 0 }}
               transition={{ duration: 0.6, delay: 0.2 }}
               viewport={{ once: true }}
             >
               <div className={styles.contact__form_group}>
                 <label htmlFor="name" className={styles.contact__label}>Name</label>
                 <input
                   type="text"
                   id="name"
                   name="name"
                   className={styles.contact__input}
                   placeholder="Your name"
                   value={formState.name}
                   onChange={handleInputChange}
                   required
                 />
               </div>

               <div className={styles.contact__form_group}>
                 <label htmlFor="email" className={styles.contact__label}>Email</label>
                 <input
                   type="email"
                   id="email"
                   name="email"
                   className={styles.contact__input}
                   placeholder="your@email.com"
                   value={formState.email}
                   onChange={handleInputChange}
                   required
                 />
               </div>

               <div className={`${styles.contact__form_group} ${styles['contact__form-group--full']}`}>
                 <label htmlFor="message" className={styles.contact__label}>Message</label>
                 <textarea
                   id="message"
                   name="message"
                   className={styles.contact__textarea}
                   placeholder="Tell me about your project..."
                   value={formState.message}
                   onChange={handleInputChange}
                   required
                 />
               </div>

               <motion.button
                 type="submit"
                 className={styles.contact__submit}
                 disabled={isLoading}
                 whileHover={{ scale: 1.02 }}
                 whileTap={{ scale: 0.98 }}
               >
                  {isLoading ? 'Sending...' : submitted ? 'Message Sent!' : error ? 'Error' : 'Send Message'}
                </motion.button>
                {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
             </motion.form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
